<template>
  <RevForm
    :initial-values="initialValues"
    method="POST"
    :validate="validate"
    @submit="handleSubmit"
  >
    <template #default="{ values, errors }">
      <RevCard class="p-24">
        <AddressFields
          v-model:first-name="values.firstName"
          v-model:last-name="values.lastName"
          v-model:phone="values.phone"
          :default-country="countryCode"
          :errors="allErrors(errors)"
          :feature-code="FeatureCode.WEB_CHECKOUT_COLLECTION_POINTS"
          :phone-help-message="i18n(translations.phoneHelperMessage)"
        />

        <div
          v-if="!withUserInformation"
          class="mt-7 flex flex-col items-stretch md:items-end"
        >
          <RevButton
            data-qa="submit-button"
            :disabled="submitting"
            full-width="responsive"
            type="submit"
            variant="primary"
          >
            {{ i18n(translations.submit) }}
          </RevButton>
        </div>
      </RevCard>

      <div v-if="withUserInformation" class="mt-8">
        <h2 class="heading-3">
          {{ i18n(translations.userInformationTitle) }}
        </h2>
        <RevCard class="mt-4 p-24 lg:mt-6">
          <UserInformationFields
            v-model:birthdate="values.birthdate"
            v-model:nationality="values.nationality"
            :errors="errors"
            :with-nationality="userInformationStore.isNationalityRequired"
          />

          <div class="mt-7 flex flex-col items-stretch md:items-end">
            <RevButton
              data-qa="submit-button"
              :disabled="submitting"
              full-width="responsive"
              type="submit"
              variant="primary"
            >
              {{ i18n(translations.submit) }}
            </RevButton>
          </div>
        </RevCard>
      </div>
    </template>
  </RevForm>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue'

import AddressFields from '@backmarket/nuxt-module-address/AddressFields.vue'
import { AddressFieldsValidators } from '@backmarket/nuxt-module-address/AddressFieldsValidators'
import { InputAddressBirthdateValidators } from '@backmarket/nuxt-module-address/InputAddressBirthdateValidators'
import { InputAddressNationalIdValidators } from '@backmarket/nuxt-module-address/InputAddressNationalIdValidators'
import { InputAddressNationalityValidators } from '@backmarket/nuxt-module-address/InputAddressNationalityValidators'
import UserInformationFields from '@backmarket/nuxt-module-address/UserInformationFields.vue'
import { FeatureCode } from '@backmarket/nuxt-module-address/featureCode'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevForm, makeValidate } from '@ds/components/Form'

import { CHECKOUT_LOG_TYPES } from '~/scopes/checkout/config'
import { useUserInformationStore } from '~/scopes/checkout/stores/userInformationStore'

import translations from './CollectionPointForm.translations'

const i18n = useI18n()
const logger = useLogger()
const {
  market: { countryCode },
} = useMarketplace()

const userInformationStore = useUserInformationStore()

const props = withDefaults(
  defineProps<{
    submitting?: boolean
    birthdate?: string
    firstName: string
    lastName: string
    phone: string
    nationality?: string
    apiErrors: { [key: string]: string }
    withUserInformation?: boolean
  }>(),
  {
    submitting: false,
    birthdate: '',
    nationality: '',
    withUserInformation: false,
  },
)

type FormValues = {
  firstName: string
  lastName: string
  phone: string
  birthdate?: string
  nationality?: string
}
const initialValues = reactive<FormValues>({
  firstName: props.firstName,
  lastName: props.lastName,
  phone: props.phone,
  ...insertIf(props.withUserInformation, {
    birthdate: props.birthdate,
    nationality: props.nationality,
  }),
})

const emit = defineEmits(['submit'])

const validate = computed(() => {
  const { phone, firstName, lastName } = AddressFieldsValidators(i18n)

  return makeValidate<typeof initialValues>({
    phone,
    firstName,
    lastName,
    ...insertIf(props.withUserInformation, {
      birthdate: InputAddressBirthdateValidators(i18n),
      nationalId: InputAddressNationalIdValidators(i18n),
      nationality: InputAddressNationalityValidators(i18n),
    }),
  })
})

const allErrors = (errors: Partial<typeof initialValues>) => {
  return { ...errors, ...props.apiErrors }
}

// Adding [birthdate] and [nationality] props as required since they are validated by the InsertIf
// but TS can't safe check the condition
const handleSubmit = (values: FormValues) => {
  const { firstName, lastName, phone, birthdate, nationality } = values

  logger.info('[CHECKOUT] Address submit', {
    address: { ...values },
    isCollectionPoint: true,
    type: CHECKOUT_LOG_TYPES.ADDRESS_SUBMIT_COLLECTION_POINT,
  })

  emit('submit', {
    firstName,
    lastName,
    phone,
    ...insertIf(props.withUserInformation, {
      birthdate,
      nationality,
    }),
  })
}
</script>
